import { partitionProcedureReports } from '@liza/fhir-utils';
import { formatCodeableConcept, type WithId } from '@medplum/core';
import type {
  CodeableConcept,
  DiagnosticReport,
  DocumentReference,
  Encounter,
  Observation,
  Procedure,
  Reference,
} from '@medplum/fhirtypes';
import type { TFunction } from 'i18next';
import { ArrowLeftIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '../../utils/date';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Item, ItemContent, ItemHeader } from '../ui/item';
import { DiagnosticReportSection } from './DiagnosticReportSection';
import { ProcedureLinkedEncounter } from './ProcedureLinkedEncounter';
import { ProcedureReportDocuments } from './ProcedureReportDocuments';

export type ProcedureDetailProps = {
  procedure: WithId<Procedure>;
  onBack?: () => void;
  /**
   * The resolved `Procedure.encounter` — the visit the procedure was performed at. Absent when
   * the procedure carries no encounter ref, or while the fetch is in flight (see
   * `isLoadingEncounter`).
   */
  encounter?: WithId<Encounter>;
  isLoadingEncounter?: boolean;
  /** Navigate to the linked encounter in the Data tab. Omit to hide the link. */
  onSelectEncounter?: (encounterId: string) => void;
  /** Open the linked encounter in the shared drawer. Omit to hide the button. */
  onOpenEncounterInDrawer?: (encounterId: string) => void;
  /**
   * Resolved `Procedure.report[]` DiagnosticReports, rendered as inline result tables. For an
   * imported lab-type procedure this is where the clinical content actually lives.
   */
  diagnosticReports?: WithId<DiagnosticReport>[];
  /**
   * Observations referenced from every report's `result[]` and (recursively) their `hasMember[]`
   * chains, keyed by id. The consumer owns the walk — see `useDiagnosticReportObservations`.
   */
  diagnosticReportObservations?: Map<string, Observation>;
  isLoadingDiagnosticReports?: boolean;
  /** One or more report/Observation fetches failed — surfaces an inline error per section. */
  hasDiagnosticReportError?: boolean;
  /**
   * Resolved `DocumentReference` entries from `Procedure.report[]`. That field is
   * `Reference<DiagnosticReport | DocumentReference>`, and imported procedures overwhelmingly
   * populate it with documents (scanned operative notes, anaesthesia evaluations) rather than
   * results-bearing reports — so these render as attachments alongside any report tables.
   */
  reportDocuments?: DocumentReference[];
  isLoadingReportDocuments?: boolean;
  /**
   * One or more document fetches failed. Surfaced inline for the same reason the results table
   * surfaces its own failures: a block that silently vanishes reads as "this procedure has no
   * documents", which is indistinguishable from the truth and worse than saying so.
   */
  hasReportDocumentError?: boolean;
  /** Navigate to a lab's detail (the Labs view) when a result row is clicked. */
  onSelectObservation?: (observation: Observation) => void;
  /**
   * Resolved display names for `Procedure.performer[*].actor.reference` values the consumer
   * fetched out-of-band. Prefers (1) the reference's own `display`, (2) this map's entry,
   * (3) the raw reference string.
   */
  performerDisplays?: Record<string, string>;
};

type DetailSection = { title: string; content: React.ReactNode };

const conceptList = (concepts: CodeableConcept[] | undefined): string[] =>
  concepts?.map((concept) => formatCodeableConcept(concept)).filter((value): value is string => !!value) ?? [];

const displayList = (refs: Reference[] | undefined): string[] =>
  refs?.map((ref) => ref.display).filter((value): value is string => !!value) ?? [];

const renderList = (values: string[]): React.ReactNode => (
  <ul className="list-inside list-disc">
    {values.map((value) => (
      <li key={value}>{value}</li>
    ))}
  </ul>
);

const textSection = (title: string, value: string | undefined): DetailSection | undefined =>
  value ? { title, content: <p>{value}</p> } : undefined;

const listSection = (title: string, values: string[]): DetailSection | undefined =>
  values.length > 0 ? { title, content: renderList(values) } : undefined;

const resolvePerformers = (procedure: Procedure, performerDisplays: Record<string, string> | undefined): string[] =>
  procedure.performer
    ?.map((performer) => {
      const ref = performer.actor?.reference;
      const name = performer.actor?.display ?? (ref ? performerDisplays?.[ref] : undefined) ?? ref;
      if (!name) return undefined;
      const role = formatCodeableConcept(performer.function);
      return role ? `${name} — ${role}` : name;
    })
    .filter((value): value is string => !!value) ?? [];

/** `performedDateTime`, or the period rendered as a range when it spans two instants. */
const performedSection = (procedure: Procedure, t: TFunction<'records'>): DetailSection | undefined => {
  const start = procedure.performedDateTime ?? procedure.performedPeriod?.start;
  if (!start) return undefined;

  const end = procedure.performedPeriod?.end;
  return {
    title: t('procedureDate'),
    content: (
      <p>
        {formatDateTime(start)}
        {end && end !== start && ` – ${formatDateTime(end)}`}
      </p>
    ),
  };
};

const notesSection = (procedure: Procedure, t: TFunction<'records'>): DetailSection | undefined => {
  const notes = procedure.note?.map((note) => note.text).filter((text): text is string => !!text) ?? [];
  if (notes.length === 0) return undefined;

  return {
    title: t('notes'),
    content: (
      <div className="space-y-2">
        {notes.map((text) => (
          <p key={text} className="whitespace-pre-wrap">
            {text}
          </p>
        ))}
      </div>
    ),
  };
};

/**
 * The scalar detail sections, in clinical reading order. Every section is omitted when its
 * source field is absent rather than rendering an empty row — an imported procedure typically
 * populates only a handful of these.
 */
const getSections = (
  procedure: Procedure,
  performerDisplays: Record<string, string> | undefined,
  t: TFunction<'records'>,
): DetailSection[] => {
  const outcome = formatCodeableConcept(procedure.outcome);

  const candidates: (DetailSection | undefined)[] = [
    performedSection(procedure, t),
    listSection(t('performedBy'), resolvePerformers(procedure, performerDisplays)),
    // `display` only, matching EncounterDetail: a Location is named, never linked to, so resolving
    // the reference would cost a fetch for a string an import almost always inlines anyway. A
    // reference-without-display drops the row rather than showing a raw `Location/<id>`.
    textSection(t('location'), procedure.location?.display),
    listSection(t('bodySite'), conceptList(procedure.bodySite)),
    listSection(t('indication'), [...conceptList(procedure.reasonCode), ...displayList(procedure.reasonReference)]),
    textSection(t('outcome'), outcome),
    listSection(t('complications'), [
      ...conceptList(procedure.complication),
      ...displayList(procedure.complicationDetail),
    ]),
    listSection(t('followUp'), conceptList(procedure.followUp)),
    notesSection(procedure, t),
  ];

  return candidates.filter((section): section is DetailSection => !!section);
};

export const ProcedureDetail = ({
  procedure,
  onBack,
  encounter,
  isLoadingEncounter = false,
  onSelectEncounter,
  onOpenEncounterInDrawer,
  diagnosticReports,
  diagnosticReportObservations,
  isLoadingDiagnosticReports = false,
  hasDiagnosticReportError = false,
  onSelectObservation,
  performerDisplays,
  reportDocuments,
  isLoadingReportDocuments = false,
  hasReportDocumentError = false,
}: ProcedureDetailProps): React.ReactElement => {
  const { t } = useTranslation('records');
  const name = formatCodeableConcept(procedure.code) || t('unknownProcedure');
  const status = procedure.status || t('unknown');
  const category = formatCodeableConcept(procedure.category) || null;
  const sections = getSections(procedure, performerDisplays, t);

  // Gate on the DiagnosticReport entries specifically, not on `report[]` being non-empty: a
  // procedure whose reports are all DocumentReferences has nothing for the results section, and
  // mounting it would show a permanently empty table. The section owns its own loading and error
  // states, so it still mounts before the reports resolve.
  //
  // Uses the same classifier the consumer fetches by, so "will any report resolve" is decided once.
  // Re-deriving it here would let the two drift — an entry the util drops would still open a
  // section that can never fill.
  const hasDiagnosticReports = partitionProcedureReports(procedure.report).diagnosticReportIds.length > 0;
  const isReportsPending = isLoadingDiagnosticReports || hasDiagnosticReportError;

  return (
    <div className="mx-auto w-full max-w-4xl p-5" data-testid="procedure-detail-view">
      {onBack && (
        <Button
          id="back-to-procedures-button"
          variant="ghost"
          onClick={onBack}
          className="mb-4 flex items-center gap-2"
        >
          <ArrowLeftIcon className="size-4" />
          <span>{t('backToProcedures')}</span>
        </Button>
      )}

      <div className="mb-6">
        <h1 className="text-2xl font-medium">{name}</h1>
        <div className="mt-2 flex items-center gap-2">
          <Badge variant="outline">{status}</Badge>
          {category && <Badge variant="outline">{category}</Badge>}
        </div>
      </div>

      <div className="space-y-4">
        {sections.map(({ title, content }) => (
          <Item key={title}>
            <ItemHeader>
              <ItemContent>
                <h2 className="text-lg font-semibold">{title}</h2>
                <div className="mt-2">{content}</div>
              </ItemContent>
            </ItemHeader>
          </Item>
        ))}

        <ProcedureLinkedEncounter
          encounter={encounter}
          isLoading={isLoadingEncounter}
          onSelectEncounter={onSelectEncounter}
          onOpenEncounterInDrawer={onOpenEncounterInDrawer}
        />

        {hasDiagnosticReports &&
          (isReportsPending ? (
            <DiagnosticReportSection
              diagnosticReport={undefined}
              observations={undefined}
              isLoading={isLoadingDiagnosticReports}
              hasError={hasDiagnosticReportError}
            />
          ) : (
            diagnosticReports?.map((report) => (
              <DiagnosticReportSection
                key={report.id}
                diagnosticReport={report}
                observations={diagnosticReportObservations}
                isLoading={false}
                onSelectObservation={onSelectObservation}
              />
            ))
          ))}

        <ProcedureReportDocuments
          documents={reportDocuments}
          isLoading={isLoadingReportDocuments}
          hasError={hasReportDocumentError}
        />
      </div>
    </div>
  );
};
